/**
 * TrainingMonitoringConsentProvider (TCX-8, K78-C)
 *
 * Mounted ONCE (inside AuthProvider). Centralizes the monitoring-consent gate
 * for the single training-launch chokepoint (`useOpenTrainingWithSession`) so
 * no per-surface wiring is needed, and renders the consent modal once.
 *
 * Popup-safety: consent state for the current user is pre-loaded on mount and
 * cached, so an ALREADY-consented launch runs `launchFn()` SYNCHRONOUSLY (the
 * existing popup-safe window.open path is preserved). Only an unconfirmed launch
 * defers to the modal; after opt-in (a fresh user gesture) the launch proceeds.
 *
 * The server mint/ingest gates remain the FINAL authority — this gate is the
 * grant UX, never the source of truth, and never persists consent locally.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '@/integrations/supabase/client';
import {
  checkTrainingMonitoringConsent,
  grantTrainingMonitoringConsent,
} from '@/lib/training/trainingMonitoringConsent';
import { TrainingMonitoringConsentModal } from '@/components/training/TrainingMonitoringConsentModal';
import { useTrainingLaunchFallbackStore } from '@/stores/trainingLaunchFallbackStore';

export interface MonitoredLaunchScope {
  technicianProfileId: string | null | undefined;
  /** Provider URL — used to offer a direct-open fallback if consent write fails. */
  targetUrl?: string | null;
}

interface GateValue {
  /**
   * Gate a monitored training launch behind durable consent.
   *  - cached-consented → runs launchFn() synchronously (popup-safe)
   *  - otherwise → opens the consent modal; opt-in → grant → launchFn();
   *    decline / write-failure → launchFn is NOT run.
   * If the consent scope cannot be established (no shop), the launch proceeds
   * and the server mint gate remains the authority.
   */
  gateMonitoredLaunch: (launchFn: MonitoredLaunchFn, scope: MonitoredLaunchScope) => void;
}

/**
 * A gated launch. Receives the tab that was opened SYNCHRONOUSLY inside the
 * user gesture (the consent modal's "Agree & continue" click) so the launcher
 * can navigate it after the async consent write instead of calling
 * window.open() post-await — which Safari popup-blocks. The cached-consent /
 * no-scope paths invoke it with no argument (they open their own tab
 * synchronously in the originating click).
 */
export type MonitoredLaunchFn = (preOpenedWindow?: Window | null) => void;

const TrainingMonitoringConsentGateContext = createContext<GateValue | null>(null);

export function useTrainingMonitoringConsentGate(): GateValue | null {
  return useContext(TrainingMonitoringConsentGateContext);
}

const key = (tpi: string, shopId: string) => `${tpi}|${shopId}`;

export function TrainingMonitoringConsentProvider({ children }: { children: ReactNode }) {
  const { user, activeShop } = useAuth();
  const consentedKeys = useRef<Set<string>>(new Set());

  const [modal, setModal] = useState<{
    open: boolean;
    launchFn: MonitoredLaunchFn | null;
    technicianProfileId: string | null;
    shopId: string | null;
    targetUrl: string | null;
    submitting: boolean;
    error: string | null;
  }>({ open: false, launchFn: null, technicianProfileId: null, shopId: null, targetUrl: null, submitting: false, error: null });

  // Pre-load the current user's consent so an already-opted-in technician is
  // never re-prompted and their launch stays synchronous (popup-safe).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user?.id || !activeShop?.id) return;
      try {
        const { data: profile } = await supabase
          .from('technician_profiles')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();
        const tpi = (profile as { id?: string } | null)?.id;
        if (!tpi) return;
        const granted = await checkTrainingMonitoringConsent({ technicianProfileId: tpi, shopId: activeShop.id });
        if (!cancelled && granted) consentedKeys.current.add(key(tpi, activeShop.id));
      } catch {
        // Conservative: leave the cache empty → the modal will prompt on launch.
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id, activeShop?.id]);

  const gateMonitoredLaunch = useCallback(
    (launchFn: MonitoredLaunchFn, scope: MonitoredLaunchScope) => {
      const tpi = scope.technicianProfileId;
      const shopId = activeShop?.id;
      // Cannot establish consent scope → proceed; server mint gate is authority.
      if (!tpi || !shopId) {
        launchFn();
        return;
      }
      if (consentedKeys.current.has(key(tpi, shopId))) {
        launchFn(); // synchronous — popup-safe
        return;
      }
      setModal({ open: true, launchFn, technicianProfileId: tpi, shopId, targetUrl: scope.targetUrl ?? null, submitting: false, error: null });
    },
    [activeShop?.id],
  );

  const closeWithoutLaunch = useCallback(() => {
    setModal((m) => ({ ...m, open: false, launchFn: null, submitting: false, error: null }));
  }, []);

  const handleOptIn = useCallback(async () => {
    const { technicianProfileId, shopId, launchFn } = modal;
    if (!technicianProfileId || !shopId) return;
    // Safari popup-safety (K108-L): open the provider tab SYNCHRONOUSLY inside
    // this click, BEFORE the async consent write. Safari invalidates the user
    // activation across the await, so navigating a window.open() after the grant
    // call is silently popup-blocked (Chrome is lenient; Safari is not). We open
    // a blank tab now and hand it to the launcher to navigate on success, or
    // close it if consent fails.
    const preOpened =
      typeof window !== 'undefined' ? window.open('about:blank', '_blank') : null;
    console.log('[Consent] opt-in: provider tab pre-opened synchronously:', !!preOpened);
    setModal((m) => ({ ...m, submitting: true, error: null }));
    const res = await grantTrainingMonitoringConsent({ technicianProfileId, shopId });
    if (!res.success) {
      // Write failed → close the placeholder, keep modal open, DO NOT launch.
      try { preOpened?.close(); } catch { /* ignore */ }
      setModal((m) => ({ ...m, submitting: false, error: res.error ?? 'Could not save your choice. Please try again.' }));
      // Do not leave the user stuck — offer a direct-open fallback when we know the URL.
      if (modal.targetUrl) {
        useTrainingLaunchFallbackStore.getState().show(modal.targetUrl, 'launch_failed');
      }
      return;
    }
    consentedKeys.current.add(key(technicianProfileId, shopId));
    setModal((m) => ({ ...m, open: false, submitting: false, error: null, launchFn: null }));
    console.log('[Consent] accepted → launching monitored training');
    // Run the launch from this opt-in gesture, navigating the pre-opened tab.
    launchFn?.(preOpened);
  }, [modal]);

  return (
    <TrainingMonitoringConsentGateContext.Provider value={{ gateMonitoredLaunch }}>
      {children}
      <TrainingMonitoringConsentModal
        open={modal.open}
        onOpenChange={(next) => { if (!next) closeWithoutLaunch(); }}
        onOptIn={handleOptIn}
        onDecline={closeWithoutLaunch}
        submitting={modal.submitting}
        error={modal.error}
      />
    </TrainingMonitoringConsentGateContext.Provider>
  );
}
